import md5 from 'crypto-js/md5';

// localStorage 键名
const REGISTERED_USERS_KEY = 'sealseek_registered_users';
const AUTH_USER_KEY = 'sealseek_auth_user';
// 旧版本单用户键，仅用于读旧数据后做平滑迁移
const LEGACY_REGISTERED_USER_KEY = 'sealseek_registered_user';

// 密码哈希盐值（公开盐，前端方案天花板）
const PASSWORD_SALT = 'SEALSEEK_AUTH_2026';

// 把任意 user 对象规范化成统一格式
// 注意：相较旧版，本次放宽 idCard 可空，便于白名单测试账号也能复用此结构
const normalizeUser = (user) => {
  if (!user) return null;
  const phone = String(user.phone || '').trim();
  if (!phone) return null;
  return {
    phone,
    idCard: String(user.idCard || '').trim(),
    passwordHash: String(user.passwordHash || ''),
    registeredAt: user.registeredAt || ''
  };
};

// 给页面用的"已登录用户"视图：剥掉密码哈希，避免泄漏到 UI 层
const toAuthedView = (user) => {
  const normalized = normalizeUser(user);
  if (!normalized) return null;
  return {
    phone: normalized.phone,
    idCard: normalized.idCard,
    registeredAt: normalized.registeredAt
  };
};

const safeParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

// 读取旧版单用户数据并迁移到新版列表结构（一次性，幂等）
const migrateLegacyUserIfNeeded = () => {
  const legacy = safeParse(localStorage.getItem(LEGACY_REGISTERED_USER_KEY));
  if (!legacy) return;
  const legacyUser = normalizeUser(legacy);
  // 旧数据没有密码哈希，没法用密码登录；但保留 idCard 信息不丢失
  if (!legacyUser) {
    localStorage.removeItem(LEGACY_REGISTERED_USER_KEY);
    return;
  }
  const list = safeParse(localStorage.getItem(REGISTERED_USERS_KEY)) || [];
  const exists = Array.isArray(list) && list.some((item) => item?.phone === legacyUser.phone);
  if (!exists) {
    const next = Array.isArray(list) ? [...list, legacyUser] : [legacyUser];
    localStorage.setItem(REGISTERED_USERS_KEY, JSON.stringify(next));
  }
  localStorage.removeItem(LEGACY_REGISTERED_USER_KEY);
};

// 读取已注册用户列表
export const getRegisteredUsers = () => {
  migrateLegacyUserIfNeeded();
  const parsed = safeParse(localStorage.getItem(REGISTERED_USERS_KEY));
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((item) => normalizeUser(item))
    .filter((item) => item !== null);
};

// 按手机号查询已注册用户（含密码哈希）
export const findRegisteredUser = (phone) => {
  const target = String(phone || '').trim();
  if (!target) return null;
  return getRegisteredUsers().find((item) => item.phone === target) || null;
};

// 新增或更新一条已注册用户
export const upsertRegisteredUser = (user) => {
  const normalized = normalizeUser(user);
  if (!normalized) return null;
  const list = getRegisteredUsers();
  const idx = list.findIndex((item) => item.phone === normalized.phone);
  if (idx >= 0) {
    list[idx] = normalized;
  } else {
    list.push(normalized);
  }
  localStorage.setItem(REGISTERED_USERS_KEY, JSON.stringify(list));
  return normalized;
};

// 计算密码哈希
export const hashPassword = (password) => {
  return md5(String(password || '') + PASSWORD_SALT).toString();
};

// 校验密码：明文 + 已存哈希
export const verifyPassword = (password, hash) => {
  if (!password || !hash) return false;
  return hashPassword(password) === hash;
};

// ====== 兼容旧 API：供 App.js / AuthPage.js 现有调用使用 ======

export const getAuthedUser = () => {
  const parsed = safeParse(localStorage.getItem(AUTH_USER_KEY));
  return toAuthedView(parsed);
};

export const saveAuthedUser = (user) => {
  const authed = toAuthedView(user);
  if (!authed) return null;
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(authed));
  return authed;
};

export const clearAuthedUser = () => {
  localStorage.removeItem(AUTH_USER_KEY);
};

// ====== 保留旧的 getRegisteredUser / saveRegisteredUser 名字，做兜底（避免外部直接 import 报错） ======

export const getRegisteredUser = () => {
  const list = getRegisteredUsers();
  return list[0] ? toAuthedView(list[0]) : null;
};

export const saveRegisteredUser = (user) => {
  const next = upsertRegisteredUser(user);
  return next ? toAuthedView(next) : null;
};
